import { useId, type CSSProperties, type ReactNode, type Ref } from 'react'
import { Gauge } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { HatchPattern } from '../../platform/charts/HatchPattern'
import { WidgetGrid } from '../../platform/grid/WidgetGrid'
import { Icon } from '../../platform/icons/Icon'
import type { LocalDate } from '../../platform/localDate'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Category } from '../categories/categoryTree'
import { headerLine, isOver, splitActive, type BudgetFilter } from './budgetRules'
import type { Envelope } from './budgetWire'
import { layoutFor, type PanelId } from './budgetLayout'
import { AllowanceBars } from './charts/AllowanceBars'
import { EnvelopeRadar } from './charts/EnvelopeRadar'
import { EnvelopeList } from './EnvelopeList'
import './budget.css'

type Props = {
  state: AsyncState<Envelope[]>
  categories: readonly Category[] | null
  today: LocalDate
  filter: BudgetFilter
  onFilter: (f: BudgetFilter) => void
  onCreate: () => void
  onResume: (e: Envelope) => void
  headingRef?: Ref<HTMLParagraphElement>
}

/** The Envelopes over tile: a count and names, from the server's flags (B2). A plain Panel, never the hero (rule 9). */
function OverTile({ active }: { active: readonly Envelope[] }) {
  const id = useId()
  const over = active.filter(isOver)
  return (
    <Panel className="env-tile" aria-labelledby={id}>
      <span id={id} className="env-tile__label">
        Envelopes over
      </span>
      <span className={over.length > 0 ? 'env-tile__figure env-tile__figure--over' : 'env-tile__figure'}>
        {`${over.length} of ${active.length}`}
      </span>
      <span className="env-tile__sub">{over.length > 0 ? over.map((e) => e.name).join(', ') : 'None over'}</span>
    </Panel>
  )
}

function Legend() {
  const hatchId = `ab-legend-${useId().replace(/:/g, '')}`
  return (
    <div className="ab-legend" aria-hidden="true">
      <span className="ab-legend__item">
        <svg width="12" height="12" viewBox="0 0 12 12">
          <rect className="ab-bar--spent" width="12" height="12" rx="2" />
        </svg>
        Spent
      </span>
      <span className="ab-legend__item">
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ '--ab-hatch': `url(#${hatchId})` } as CSSProperties}>
          <defs>
            <HatchPattern id={hatchId} groundClass="ab-hatch__ground" stripeClass="ab-hatch__stripe" />
          </defs>
          <rect className="ab-legend__hatch" width="12" height="12" rx="2" />
        </svg>
        Left
      </span>
    </div>
  )
}

function Body({ envelopes, ...p }: Omit<Props, 'state' | 'onCreate' | 'headingRef'> & { envelopes: Envelope[] }) {
  const bp = useBreakpoint()
  const phone = bp === 'phone'
  const barsId = useId()
  const radarId = useId()
  const { active, paused } = splitActive(envelopes)
  // An opaque content tile like the other panels (rule 9: only floating things are glass).
  const list = (
    <Panel className="budget-panel">
      <EnvelopeList
        active={active}
        paused={paused}
        categories={p.categories}
        filter={p.filter}
        onFilter={p.onFilter}
        onResume={p.onResume}
      />
    </Panel>
  )
  const tile = <OverTile active={active} />
  const bars = (
    <Panel className="budget-panel" aria-labelledby={barsId}>
      <div className="budget-panel__head">
        <div>
          <h2 id={barsId} className="budget-panel__title">
            Spent vs allowance
          </h2>
          <p className="budget-panel__sub">Solid is spent, hatched is what&apos;s still yours</p>
        </div>
        <Legend />
      </div>
      <div className="wg-scroll">
        <AllowanceBars envelopes={active} phone={phone} labelledBy={barsId} />
      </div>
    </Panel>
  )
  const radar = phone ? null : (
    <Panel className="budget-panel" aria-labelledby={radarId}>
      <h2 id={radarId} className="budget-panel__title">
        Envelope shape
      </h2>
      <p className="budget-panel__sub">Used against plan — outside the ring is over</p>
      <div className="wg-scroll wg-scroll--fit">
        <EnvelopeRadar envelopes={active} labelledBy={radarId} />
      </div>
    </Panel>
  )
  // M4-R19: the list leads on a phone (budgetLayout.ts); the grid owns the order and the sizes.
  const panels: Record<PanelId, ReactNode> = { over: tile, bars, radar, list }
  return <WidgetGrid items={layoutFor(bp)} render={(id) => panels[id]} />
}

export function BudgetScreen({ state, onCreate, headingRef, ...rest }: Props) {
  const bp = useBreakpoint()
  const newButton = (
    <Button variant="primary" size="page" onClick={onCreate}>
      + New envelope
    </Button>
  )
  return (
    <div className={`budget-page budget-page--${bp}`}>
      <div className="budget-head">
        {/* The visual order IS the DOM order (focus follows it): on a phone the action leads. */}
        {bp === 'phone' && newButton}
        <p ref={headingRef} tabIndex={-1} className="budget-head__line">
          {headerLine(rest.today)}
        </p>
        {bp !== 'phone' && newButton}
      </div>
      <Async
        surface="budgets"
        state={state}
        skeleton={
          <div className="budget-body">
            <Skeleton height={96} />
            <Skeleton height={220} />
            <Skeleton height={260} />
          </div>
        }
        empty={{
          icon: <Icon glyph={Gauge} size={24} />,
          title: 'No envelopes yet',
          body: "Give each category a monthly allowance and see what's left.",
          action: { label: 'New envelope', onAct: onCreate },
        }}
        announce={(d) => {
          const n = splitActive(d).active.length
          return n === 1 ? '1 envelope' : `${n} envelopes`
        }}
      >
        {(envelopes) => <Body envelopes={envelopes} {...rest} />}
      </Async>
    </div>
  )
}
